import type { ApolloError } from '@apollo/client';
import type { GetSessionDataQuery } from '@predium/client-graphql';
import { usePostHog } from '@posthog/react';
import { useSelector } from '@xstate/store-react';
import isNil from 'lodash/isNil';
import { useEffect } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useAccessToken } from '@/components/session/AccessTokenContext';
import { sessionStore } from '@/components/session/session.store';
import usePosthogTracking from '@/hooks/usePosthogTracking';

/**
 * Feed the session store from the `GET_SESSION_DATA` query result:
 * hydrate on success, record the error on failure.
 */
export function useHydrateSessionStore(data: GetSessionDataQuery | undefined, error: ApolloError | undefined) {
  useEffect(() => {
    if (isNil(data?.getSessionData)) return;
    sessionStore.send({ type: 'hydrate', data: data.getSessionData });
  }, [data]);

  useEffect(() => {
    if (isNil(error)) return;
    sessionStore.send({ type: 'setError', error });
  }, [error]);
}

/**
 * Identify the current user in PostHog (user properties, people profile, org group)
 * whenever session data becomes available or changes.
 */
export function useIdentifyPosthogUser() {
  const posthog = usePostHog();
  const user = useSelector(sessionStore, (s) => s.context.user);
  const org = useSelector(sessionStore, (s) => s.context.org);
  const isUnauthenticated = useSelector(sessionStore, (s) => s.context.status === 'unauthenticated');

  useEffect(() => {
    if (isNil(posthog) || isNil(user) || isNil(org)) return;

    posthog.identify(user.id.toString(), {
      email: user.email,
      first_name: user.firstName,
      last_name: user.lastName,
      orgId: org.id,
      orgName: org.name,
    });
    posthog.people.set({
      orgId: org.id,
      orgName: org.name,
      orgStatus: org.statusTypeId,
      orgType: org.orgTypeId,
    });
    posthog.group('orgId', org.id.toString());
  }, [org, posthog, user]);

  useEffect(() => {
    if (isNil(posthog)) return;
    if (isUnauthenticated) {
      posthog.reset();
    }
  }, [isUnauthenticated, posthog]);
}

/**
 * Attach the current user to the Upscope cobrowsing session whenever session data is available.
 */
export function useUpscopeConnection() {
  const user = useSelector(sessionStore, (s) => s.context.user);

  useEffect(() => {
    if (isNil(user) || isNil(window.Upscope)) return;

    window.Upscope('updateConnection', {
      uniqueId: user.id.toString(),
      identities: [`${user.firstName} ${user.lastName}`, user.email ?? ''],
    });
  }, [user]);
}

/**
 * Log the user out after the org-configured idle timeout elapses.
 * No-op when the user is unauthenticated or the org has no timeout configured.
 */
export function useLogoutOnIdle() {
  const { logout } = useAccessToken();
  const { trackEvent } = usePosthogTracking();

  const userLogoutIdleTimeout = useSelector(sessionStore, (s) =>
    s.context.status === 'authenticated' ? (s.context.org?.userLogoutIdleTimeout ?? null) : null,
  );

  useIdleTimer({
    onIdle: () => {
      trackEvent('USER_LOGOUT_IDLE_TIMEOUT_EXPIRED');
      logout();
    },
    disabled: isNil(userLogoutIdleTimeout) || userLogoutIdleTimeout <= 0,
    // Minimum timeout of 1 second because the timeout can't be zero even if it's disabled
    timeout: Math.max((userLogoutIdleTimeout ?? 0) * 1000, 1000),
    crossTab: true,
    leaderElection: true,
    syncTimers: 200,
  });
}
